<template>
  <v-card outlined class="queue-lane" :class="`queue-lane--${laneKey}`" :data-lane="laneKey">
    <v-toolbar dense flat class="queue-lane__toolbar">
      <v-icon small left color="primary">{{ icon }}</v-icon>
      <v-toolbar-title class="subtitle-1 queue-lane__title">{{ title }}</v-toolbar-title>
      <v-chip
        x-small
        class="ml-2 queue-lane__count"
        :color="runs.length ? 'primary' : 'grey lighten-2'"
        :dark="runs.length > 0"
      >
        {{ runs.length }}
      </v-chip>
      <v-spacer />
      <slot name="toolbar"></slot>
    </v-toolbar>
    <v-divider />

    <div class="queue-lane__body">
      <v-skeleton-loader
        v-if="loading && !runs.length"
        type="list-item-two-line@4"
        class="queue-lane__skeleton"
      />

      <div v-else-if="!runs.length" class="queue-lane__empty text-center py-8 px-4">
        <v-icon size="40" color="grey lighten-1">{{ emptyIcon }}</v-icon>
        <div class="body-2 grey--text text--darken-1 mt-2">{{ emptyText }}</div>
      </div>

      <draggable
        v-else-if="draggable"
        :value="runs"
        :disabled="dragDisabled"
        handle=".queue-lane__drag-handle"
        ghost-class="queue-lane__ghost"
        chosen-class="queue-lane__chosen"
        animation="150"
        class="queue-lane__draggable"
        @change="onDragChange"
      >
        <div
          v-for="(run, index) in runs"
          :key="runKey(run, index)"
          class="queue-lane__item"
          :data-run-id="runKey(run, index)"
        >
          <v-icon
            small
            class="queue-lane__drag-handle"
            :class="{ 'queue-lane__drag-handle--disabled': dragDisabled }"
            title="Drag to reorder"
            aria-label="Drag to reorder"
            >mdi-drag-vertical</v-icon
          >
          <div class="queue-lane__item-content">
            <slot :run="run" :index="index"></slot>
          </div>
        </div>
      </draggable>

      <template v-else>
        <slot v-for="(run, index) in runs" :run="run" :index="index"></slot>
      </template>
    </div>
  </v-card>
</template>

<script>
import draggable from 'vuedraggable';
import { runIdOf } from '@/store/modules/analysisQueue';

/**
 * One of the three queue lanes (Waiting / Running / Recent): toolbar with count, skeleton while
 * loading, English empty state and a default slot rendered once per run.
 *
 * With `draggable` (Waiting lane, M3 task F12b) each run gets a drag handle; dropping emits
 * `reorder` `{ run, runId, oldIndex, newIndex }` and leaves the order to the parent (optimistic
 * store update + PATCH position), so the lane never diverges from the store.
 */
export default {
  name: 'QueueLane',

  components: { draggable },

  props: {
    laneKey: {
      type: String,
      required: true,
    },
    title: {
      type: String,
      required: true,
    },
    icon: {
      type: String,
      default: 'mdi-tray',
    },
    runs: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    emptyText: {
      type: String,
      default: 'Queue is empty',
    },
    emptyIcon: {
      type: String,
      default: 'mdi-tray',
    },
    /** Enable drag and drop reordering (waiting lane only). */
    draggable: {
      type: Boolean,
      default: false,
    },
    /** Keep the handles but refuse drags (e.g. while a reorder request is in flight). */
    dragDisabled: {
      type: Boolean,
      default: false,
    },
  },

  methods: {
    runKey(run, index) {
      return runIdOf(run) || `run-${index}`;
    },

    /** vuedraggable `change` payload: { moved: { element, oldIndex, newIndex } } for same-list moves. */
    onDragChange(event) {
      const moved = event && event.moved;
      if (!moved || moved.oldIndex === moved.newIndex) return;
      this.$emit('reorder', {
        run: moved.element,
        runId: runIdOf(moved.element),
        oldIndex: moved.oldIndex,
        newIndex: moved.newIndex,
      });
    },
  },
};
</script>

<style scoped>
.queue-lane {
  display: flex;
  flex-direction: column;
  height: 100%;
}
.queue-lane__title {
  font-weight: 500;
}
.queue-lane__body {
  padding: 8px;
  flex: 1 1 auto;
  min-height: 160px;
}
.queue-lane__item {
  display: flex;
  align-items: stretch;
}
.queue-lane__drag-handle {
  cursor: grab;
  align-self: center;
  margin-right: 2px;
  color: rgba(0, 0, 0, 0.38);
}
.queue-lane__drag-handle:active {
  cursor: grabbing;
}
.queue-lane__drag-handle--disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
.queue-lane__item-content {
  flex: 1 1 auto;
  min-width: 0;
}
.queue-lane__ghost {
  opacity: 0.4;
  background: rgba(98, 0, 234, 0.06);
  border-radius: 4px;
}
.queue-lane__chosen {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.16);
}
</style>
